/**
 * AnalyticsView — Website traffic dashboard (custom, self-hosted, RGPD-friendly).
 * Shows pageviews, unique visitors, traffic sources, top pages, devices,
 * conversion funnel, and a time series chart.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  BarChart3, Users, TrendingUp, MousePointerClick, Smartphone, Globe,
  Activity, ArrowUpRight, ArrowDownRight, Minus, RefreshCw
} from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
  LineChart, Line, Area, AreaChart,
} from 'recharts';
import { Button } from '../../ui/button';

const API = `${window.location.origin}/api`;

// Phocea brand palette — neon yellow + supportive colors
const BRAND = '#DFFF00';
const CHART_COLORS = ['#DFFF00', '#A3E635', '#FACC15', '#F97316', '#EC4899', '#8B5CF6', '#3B82F6', '#06B6D4', '#14B8A6', '#64748B'];

const PERIODS = [
  { id: 'today', label: "Aujourd'hui" },
  { id: '7d', label: '7 jours' },
  { id: '30d', label: '30 jours' },
  { id: '90d', label: '90 jours' },
];

const Growth = ({ value }) => {
  if (value > 0) return (
    <span className="flex items-center gap-1 text-xs text-green-400 bg-green-500/15 px-2 py-0.5 rounded-full">
      <ArrowUpRight className="w-3 h-3" />+{value}%
    </span>
  );
  if (value < 0) return (
    <span className="flex items-center gap-1 text-xs text-red-400 bg-red-500/15 px-2 py-0.5 rounded-full">
      <ArrowDownRight className="w-3 h-3" />{value}%
    </span>
  );
  return (
    <span className="flex items-center gap-1 text-xs text-slate-400 bg-slate-500/15 px-2 py-0.5 rounded-full">
      <Minus className="w-3 h-3" />0%
    </span>
  );
};

const KPI = ({ icon: Icon, label, value, suffix, growth, accent }) => (
  <div className="bg-slate-900 rounded-xl border border-slate-800 p-5 hover:border-[#DFFF00]/30 transition-colors">
    <div className="flex items-center justify-between mb-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent || 'bg-[#DFFF00]/15 text-[#DFFF00]'}`}>
        <Icon className="w-5 h-5" />
      </div>
      {typeof growth === 'number' && <Growth value={growth} />}
    </div>
    <p className="text-3xl font-bold text-white tabular-nums">
      {value}{suffix ? <span className="text-lg text-slate-400 ml-1">{suffix}</span> : null}
    </p>
    <p className="text-sm text-slate-400 mt-1">{label}</p>
  </div>
);

const ChartCard = ({ title, children, subtitle }) => (
  <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
    <div className="mb-4">
      <h3 className="text-white font-semibold">{title}</h3>
      {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const EmptyState = ({ text }) => (
  <div className="flex items-center justify-center h-64 text-sm text-slate-500">
    {text}
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs shadow-xl">
      {label && <p className="text-slate-400 mb-1">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} className="text-white">
          <span style={{ color: p.color || p.fill }}>●</span> {p.name} : <span className="font-bold">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

const AnalyticsView = () => {
  const [period, setPeriod] = useState('7d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchSummary = useCallback(async (p) => {
    try {
      const res = await axios.get(`${API}/admin/analytics/summary?period=${p}`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      console.error('analytics fetch error', err);
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchSummary(period);
  }, [period, fetchSummary]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96 text-slate-400">
        <Activity className="w-6 h-6 mr-2 animate-pulse text-[#DFFF00]" />
        Chargement des statistiques...
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-12 text-center">
        <BarChart3 className="w-12 h-12 mx-auto text-slate-700 mb-3" />
        <p className="text-white font-semibold mb-1">Aucune donnée disponible</p>
        <p className="text-sm text-slate-500">Les statistiques apparaîtront dès que des visiteurs navigueront sur le site.</p>
      </div>
    );
  }

  const { kpis, sources = [], top_pages: topPages = [], devices = [], timeseries = [], funnel } = data;
  const hasAnyData = (kpis?.pageviews || 0) > 0;

  return (
    <div className="space-y-6" data-testid="analytics-view">
      {/* Header + period selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white">Trafic & Analytics</h2>
          <p className="text-sm text-slate-400 mt-1">Mesurez votre audience, vos sources et votre conversion en temps réel.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center bg-slate-900 border border-slate-800 rounded-lg p-1">
            {PERIODS.map(p => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                data-testid={`analytics-period-${p.id}`}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  period === p.id
                    ? 'bg-[#DFFF00] text-black'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
            onClick={() => { setRefreshing(true); fetchSummary(period); }}
            data-testid="analytics-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Rafraîchir
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={MousePointerClick} label="Pages vues" value={kpis.pageviews.toLocaleString('fr-FR')} growth={kpis.pageviews_growth} />
        <KPI icon={Users} label="Visiteurs uniques" value={kpis.visitors.toLocaleString('fr-FR')} growth={kpis.visitors_growth} accent="bg-emerald-500/15 text-emerald-400" />
        <KPI icon={TrendingUp} label="Réservations" value={kpis.reservations.toLocaleString('fr-FR')} accent="bg-orange-500/15 text-orange-400" />
        <KPI icon={BarChart3} label="Taux de conversion" value={kpis.conversion_rate} suffix="%" accent="bg-pink-500/15 text-pink-400" />
      </div>

      {!hasAnyData && (
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6 text-center">
          <p className="text-slate-400 text-sm">
            📊 Aucun événement tracké pour cette période. Les données arriveront dès que des visiteurs naviguent sur phocearent.fr.
            <br />
            <span className="text-xs text-slate-500">Le tracker est actif et collecte silencieusement les pages vues.</span>
          </p>
        </div>
      )}

      {/* Traffic over time */}
      <ChartCard title="Évolution du trafic" subtitle="Pages vues et visiteurs uniques par jour">
        {timeseries.length === 0 ? (
          <EmptyState text="Aucune donnée pour cette période" />
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={timeseries}>
              <defs>
                <linearGradient id="gradPageviews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={BRAND} stopOpacity={0.6} />
                  <stop offset="100%" stopColor={BRAND} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradVisitors" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#A3E635" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#A3E635" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12, color: '#94a3b8' }} />
              <Area type="monotone" dataKey="pageviews" name="Pages vues" stroke={BRAND} fill="url(#gradPageviews)" strokeWidth={2} />
              <Area type="monotone" dataKey="visitors" name="Visiteurs" stroke="#A3E635" fill="url(#gradVisitors)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      {/* Two columns: Sources pie + Devices pie */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Sources de trafic" subtitle="D'où viennent vos visiteurs ?">
          {sources.length === 0 ? (
            <EmptyState text="Aucune source trackée" />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={sources}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  label={(entry) => `${entry.name} (${entry.value})`}
                >
                  {sources.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="#0f172a" strokeWidth={2} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Appareils" subtitle="Répartition desktop / mobile / tablette">
          {devices.length === 0 ? (
            <EmptyState text="Aucun appareil détecté" />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={devices}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={110}
                  paddingAngle={2}
                  label={(entry) => `${entry.name} (${entry.value})`}
                >
                  {devices.map((_, i) => (
                    <Cell key={i} fill={[BRAND, '#A3E635', '#FACC15'][i % 3]} stroke="#0f172a" strokeWidth={2} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Top pages */}
      <ChartCard title="Pages les plus vues" subtitle="Top 10 des URLs de votre site">
        {topPages.length === 0 ? (
          <EmptyState text="Aucune page vue" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-800">
                  <th className="py-2 pr-4 font-medium">#</th>
                  <th className="py-2 pr-4 font-medium">Page</th>
                  <th className="py-2 pr-4 font-medium text-right">Vues</th>
                  <th className="py-2 font-medium text-right">Visiteurs uniques</th>
                </tr>
              </thead>
              <tbody>
                {topPages.map((p, i) => {
                  const maxViews = topPages[0].views || 1;
                  const pct = (p.views / maxViews) * 100;
                  return (
                    <tr key={p.path} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                      <td className="py-2.5 pr-4 text-slate-500">{i + 1}</td>
                      <td className="py-2.5 pr-4 text-white truncate max-w-md font-mono text-xs">
                        <div className="flex items-center gap-2">
                          <span className="truncate">{p.path || '/'}</span>
                        </div>
                        <div className="h-1 bg-slate-800 rounded-full mt-1 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: BRAND }} />
                        </div>
                      </td>
                      <td className="py-2.5 pr-4 text-right text-white tabular-nums">{p.views.toLocaleString('fr-FR')}</td>
                      <td className="py-2.5 text-right text-slate-400 tabular-nums">{p.unique_visitors.toLocaleString('fr-FR')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </ChartCard>

      {/* Conversion funnel */}
      {funnel && (
        <ChartCard title="Entonnoir de conversion" subtitle="Du visiteur à la réservation payée">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {[
              { label: 'Visiteurs', value: funnel.visitors, icon: Users, color: BRAND },
              { label: 'Checkout démarré', value: funnel.started_checkout, icon: MousePointerClick, color: '#A3E635' },
              { label: 'Checkout complété', value: funnel.completed_checkout, icon: TrendingUp, color: '#FACC15' },
              { label: 'Réservations en base', value: funnel.db_reservations, icon: BarChart3, color: '#F97316' },
            ].map((step, i, arr) => {
              const base = arr[0].value || 1;
              const pct = Math.round((step.value / base) * 100);
              return (
                <div key={step.label} className="bg-slate-950 rounded-lg p-4 border border-slate-800">
                  <div className="flex items-center gap-2 mb-2">
                    <step.icon className="w-4 h-4" style={{ color: step.color }} />
                    <span className="text-xs text-slate-400">{step.label}</span>
                  </div>
                  <p className="text-2xl font-bold text-white tabular-nums">{step.value.toLocaleString('fr-FR')}</p>
                  <div className="mt-2 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: step.color }} />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{pct}% du top</p>
                </div>
              );
            })}
          </div>
        </ChartCard>
      )}
    </div>
  );
};

export default AnalyticsView;
